import { useState } from "react";
import { Check, Loader2, FileText, Users } from "lucide-react";
import { usePricing, productOf, fmt, checkoutHref, bookingHref } from "../lib/catalog";
import CtaLink, { btnPrimary, btnDark, btnOutline, btnOrange } from "./CtaLink";

function Toggle({ value, onChange, options, testid }) {
  return (
    <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid={testid}>
      {options.map(([v, label, disabled]) => (
        <button key={v} type="button" disabled={disabled} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
          className={`px-4 h-9 rounded-full text-sm font-semibold transition-colors ${value === v ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function BlueprintBanner({ p }) {
  return (
    <div className="rounded-2xl border border-orange-200 bg-orange-50/60 p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-5" data-testid="blueprint-banner">
      <div className="h-11 w-11 rounded-xl bg-orange-100 flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-orange-600" /></div>
      <div className="flex-1">
        <p className="font-mono text-[11px] uppercase tracking-widest text-orange-600">Not ready to commit? Start here</p>
        <h3 className="font-heading font-bold text-slate-900 mt-1">{p.name} — {fmt(p.price)} one-time</h3>
        <p className="text-sm text-slate-600 mt-1">{p.credit_note} Includes: {p.features.join(", ").toLowerCase()}. {p.delivery}</p>
      </div>
      <CtaLink to={checkoutHref(p)} className={`${btnOrange} shrink-0`} testid="blueprint-cta" event={{ name: "pricing.cta", props: { plan: "blueprint" } }}>{p.cta}</CtaLink>
    </div>
  );
}

function PriceBlock({ p, optionId }) {
  if (p.type === "custom_quote") {
    return <div className="mt-5"><span className="font-heading text-3xl font-bold text-slate-900">From {fmt(p.setup_price)}</span><span className="text-slate-400 text-sm"> setup</span><p className="text-sm text-indigo-600 font-medium mt-1">+ {fmt(p.monthly_price)}/month</p></div>;
  }
  const main = optionId === "plan" ? `${p.installments} × ${fmt(p.installment_amount)}` : fmt(p.setup_price);
  const sub = optionId === "annual" ? `+ ${fmt(p.annual_price)}/year monitoring (2 months free)` : `+ ${fmt(p.monthly_price)}/month monitoring${p.code === "agentic_ready" ? " & growth consultancy" : ""}`;
  return (
    <div className="mt-5" data-testid={`price-${p.code}`}>
      <span className="font-heading text-4xl font-bold text-slate-900">{main}</span>
      <span className="text-slate-400 text-sm"> {optionId === "plan" ? "monthly payments" : "setup"}</span>
      <p className="text-sm text-indigo-600 font-medium mt-1">{sub}</p>
    </div>
  );
}

function TierCard({ p, optionId, booking, compact }) {
  const popular = p.badge === "MOST POPULAR";
  return (
    <div data-testid={`pricing-plan-${p.code}`}
      className={`relative rounded-2xl bg-white p-8 flex flex-col transition-transform duration-300 ${popular ? "border-2 border-indigo-500 shadow-2xl shadow-indigo-500/20 lg:-translate-y-4 lg:scale-[1.04] z-10" : "border border-slate-200 hover:-translate-y-1"}`}>
      {p.badge && <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-mono font-semibold tracking-wide ${popular ? "bg-indigo-600 text-white" : "bg-slate-900 text-white"}`}>{p.badge}</div>}
      <h3 className="font-heading text-xl font-bold text-slate-900">{p.name}</h3>
      <p className="text-sm text-slate-500 mt-1 min-h-10">{p.promise}</p>
      <PriceBlock p={p} optionId={optionId} />
      {p.type === "custom_quote" ? (
        <CtaLink to={bookingHref(booking.strategy_call_url)} className={`${btnDark} mt-6 w-full`} testid={`book-${p.code}`} event={{ name: "pricing.cta", props: { plan: p.code } }}>Book a Strategy Call</CtaLink>
      ) : (
        <CtaLink to={checkoutHref(p, optionId)} className={`${popular ? btnPrimary : btnDark} mt-6 w-full`} testid={`select-plan-${p.code}`} event={{ name: "pricing.cta", props: { plan: p.code, option: optionId } }}>{p.cta}</CtaLink>
      )}
      {popular && <CtaLink to={bookingHref(booking.score_review_url)} className={`${btnOutline} mt-3 w-full`} testid="book-score-review">Book a 15-min Score Review</CtaLink>}
      {!compact && (
        <ul className="mt-7 space-y-3">
          {p.features.map((f, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /><span>{f}</span></li>)}
        </ul>
      )}
    </div>
  );
}

export function Founding25({ f }) {
  return (
    <div className="mt-12 rounded-2xl bg-slate-950 text-white p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-5" data-testid="founding25-band">
      <div className="h-11 w-11 rounded-xl bg-indigo-500/20 flex items-center justify-center shrink-0"><Users className="h-5 w-5 text-indigo-300" /></div>
      <p className="flex-1 text-slate-200"><span className="font-heading font-bold text-white">Founding 25:</span> The first 25 clients lock today's pricing for life in exchange for a case study and testimonial.</p>
      <p className="font-heading text-lg font-bold text-orange-400 shrink-0" data-testid="founding25-remaining">{f.remaining} of {f.total} spots remaining</p>
    </div>
  );
}

export default function PricingSection({ compact = false }) {
  const { data, error, reload } = usePricing();
  const [pay, setPay] = useState("full");
  const [mon, setMon] = useState("monthly");
  if (error) return <div className="text-center py-16" data-testid="pricing-error"><p className="font-heading text-xl font-bold text-slate-900">We couldn't load the plans just now.</p><button onClick={reload} className={`${btnPrimary} mt-5`} data-testid="pricing-retry">Try again</button></div>;
  if (!data) return <div className="flex flex-col items-center py-24 text-slate-500" data-testid="pricing-loading"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /><p className="mt-4 text-sm">Loading plans…</p></div>;
  const optionId = mon === "annual" ? "annual" : pay;
  const tiers = ["get_found", "agentic_ready", "autonomous_commerce"].map((c) => productOf(data, c)).filter(Boolean);
  return (
    <div data-testid="pricing-section">
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
        <Toggle testid="toggle-pay" value={mon === "annual" ? "full" : pay} onChange={setPay} options={[["full", "Pay in full"], ["plan", "Payment plan", mon === "annual"]]} />
        <Toggle testid="toggle-monitoring" value={mon} onChange={(v) => { setMon(v); if (v === "annual") setPay("full"); }} options={[["monthly", "Monthly"], ["annual", "Annual (2 months free)"]]} />
      </div>
      {mon === "annual" && <p className="text-center text-xs text-slate-500 -mt-5 mb-6" data-testid="annual-note">Annual monitoring is paired with pay-in-full setup.</p>}
      <BlueprintBanner p={productOf(data, "blueprint")} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch mt-12">
        {tiers.map((p) => <TierCard key={p.code} p={p} optionId={optionId} booking={data.booking} compact={compact} />)}
      </div>
      <Founding25 f={data.founding25} />
    </div>
  );
}
